import {
  LABELS,
  GATES,
  isSequential,
  isFF,
  pins,
  sourceKey,
  allSignals,
  CLOCK_MODES,
} from "./registry";
export function combinationalOrder(project) {
  const nodes = project.components.filter(
      (c) => !isSequential(c) && c.type !== "CLOCK",
    ),
    ids = new Set(nodes.map((c) => c.id));
  const deps = new Map(
    nodes.map((c) => [
      c.id,
      new Set(
        project.connections
          .filter((e) => e.to.component === c.id && ids.has(e.from.component))
          .map((e) => e.from.component),
      ),
    ]),
  );
  const result = [],
    done = new Set();
  let changed = true;
  while (changed) {
    changed = false;
    for (const c of nodes)
      if (!done.has(c.id) && [...deps.get(c.id)].every((id) => done.has(id))) {
        result.push(c);
        done.add(c.id);
        changed = true;
      }
  }
  return { order: result, cyclic: nodes.filter((c) => !done.has(c.id)) };
}
export function validate(project) {
  const issues = [];
  const add = (severity, message, component, wire) =>
    issues.push({ severity, message, component, wire });
  const nodes = new Map(project.components.map((c) => [c.id, c]));
  const inputIds = new Set(project.inputs.map((i) => i.id));
  const allIds = [
    ...project.components.map((c) => c.id),
    ...project.inputs.map((i) => i.id),
    ...project.connections.map((e) => e.id),
  ];
  if (new Set(allIds).size !== allIds.length)
    add(
      "error",
      "Duplicate identifiers. Each component, terminal and wire needs a unique ID.",
    );
  const names = [
    ...project.inputs.map((i) => i.name),
    ...project.components.map((c) => c.name),
  ];
  if (new Set(names).size !== names.length)
    add(
      "error",
      "Duplicate names. Rename components and input terminals so labels are unambiguous.",
    );
  if (names.some((n) => !n?.trim()))
    add("error", "Every component and primary input needs a name.");
  if (project.components.length === 0)
    add("error", "Add at least one component to the circuit.");
  const driver = new Map();
  function validSource(s) {
    if (s.input) return inputIds.has(s.input);
    if ("constant" in s) return [0, 1, "X"].includes(s.constant);
    const c = nodes.get(s.component);
    return !!c && pins(c).outputs.some((p) => p.id === s.pin);
  }
  for (const e of project.connections) {
    const dest = nodes.get(e.to.component),
      key = `${e.to.component}:${e.to.pin}`;
    if (!validSource(e.from))
      add(
        "error",
        "Wire source does not refer to an existing output or input terminal. Reconnect or delete it.",
        e.to.component,
        e.id,
      );
    if (!dest || !pins(dest).inputs.some((p) => p.id === e.to.pin))
      add(
        "error",
        "Wire destination must be an existing input pin.",
        e.to.component,
        e.id,
      );
    if (e.width !== undefined && e.width !== 1)
      add(
        "error",
        "This connection must be one bit wide. Connect multibit blocks using their individual bit pins.",
        e.to.component,
        e.id,
      );
    if (driver.has(key))
      add(
        "error",
        `Multiple drivers on ${dest?.name || "?"}:${e.to.pin}. Keep one source per input pin.`,
        e.to.component,
        e.id,
      );
    driver.set(key, e.from);
  }
  for (const c of project.components) {
    const cfg = c.config;
    if (!Object.prototype.hasOwnProperty.call(LABELS, c.type)) {
      add("error", `Unsupported component type ${c.type}.`, c.id);
      continue;
    }
    if (!Number.isFinite(c.x) || !Number.isFinite(c.y))
      add("error", `${c.name}: position must be finite.`, c.id);
    if (
      GATES.includes(c.type) &&
      c.type !== "NOT" &&
      (!Number.isInteger(cfg.inputs) || cfg.inputs < 2 || cfg.inputs > 8)
    )
      add("error", `${c.name}: gate input count must be 2–8.`, c.id);
    if (c.type === "NOT" && cfg.inputs !== 1)
      add("error", `${c.name}: NOT requires exactly one input.`, c.id);
    if (["MUX", "DEMUX"].includes(c.type) && ![2, 4, 8].includes(cfg.size))
      add("error", `${c.name}: size must be 2, 4 or 8.`, c.id);
    if (["ENCODER", "PRIORITY"].includes(c.type) && ![4, 8].includes(cfg.size))
      add("error", `${c.name}: encoder size must be 4 or 8.`, c.id);
    if (c.type === "DECODER" && ![2, 3].includes(cfg.size))
      add("error", `${c.name}: decoder address width must be 2 or 3.`, c.id);
    if (
      ["PA", "COMPARE"].includes(c.type) &&
      (!Number.isInteger(cfg.width) || cfg.width < 1 || cfg.width > 8)
    )
      add("error", `${c.name}: bit width must be 1–8.`, c.id);
    if (isSequential(c)) {
      if (![0, 1, "X"].includes(cfg.initial))
        add("error", `${c.name}: initial state must be 0, 1 or X.`, c.id);
      if (!["POS", "NEG"].includes(cfg.trigger))
        add(
          "error",
          `${c.name}: choose positive or negative activation.`,
          c.id,
        );
      if (
        c.type.startsWith("SR") &&
        !["X", "0", "1", "HOLD", 0, 1].includes(cfg.policy)
      )
        add(
          "error",
          `${c.name}: select a valid SR forbidden-state policy.`,
          c.id,
        );
    }
    if (c.type === "CLOCK") {
      if (cfg.activation && !CLOCK_MODES[cfg.activation])
        add("error", `${c.name}: choose one of the four clock types.`, c.id);
      if (
        !Number.isFinite(cfg.period) ||
        cfg.period < 0.1 ||
        cfg.period > 10000 ||
        !Number.isFinite(cfg.duty) ||
        cfg.duty < 0.01 ||
        cfg.duty > 0.99 ||
        !Number.isFinite(cfg.phase) ||
        cfg.phase < 0 ||
        ![0, 1].includes(cfg.initial)
      )
        add(
          "error",
          `${c.name}: use period 0.1–10000, duty 1–99%, nonnegative phase and a binary initial level.`,
          c.id,
        );
    }
    for (const p of pins(c).inputs)
      if (!driver.has(`${c.id}:${p.id}`))
        add(
          "error",
          `${c.name}.${p.id} is unconnected. Assign a terminal or connect an output.`,
          c.id,
        );
    if (isFF(c)) {
      const s = driver.get(`${c.id}:CLK`);
      if (s && nodes.get(s.component)?.type !== "CLOCK")
        add(
          "warning",
          `${c.name} uses a derived clock. Ideal zero-delay event ordering applies; physical glitches are not modeled.`,
          c.id,
        );
    }
  }
  const order = combinationalOrder(project);
  order.cyclic.forEach((c) =>
    add(
      "error",
      `${c.name} is in or depends on a combinational loop. Break the loop with an edge-triggered state element.`,
      c.id,
    ),
  );
  const observed = new Set(
    project.observables
      .filter((o) => o.source.component)
      .map((o) => o.source.component),
  );
  project.observables.forEach((o) => {
    if (!validSource(o.source))
      add(
        "error",
        "An observed signal has a missing source. Remove it or choose a valid output.",
      );
  });
  let changes = true;
  while (changes) {
    changes = false;
    for (const e of project.connections) {
      if (
        observed.has(e.to.component) &&
        e.from.component &&
        !observed.has(e.from.component)
      ) {
        observed.add(e.from.component);
        changes = true;
      }
    }
  }
  for (const c of project.components)
    if (c.type !== "CLOCK" && !observed.has(c.id))
      add(
        "warning",
        `${c.name} does not reach a selected observable. Enable one of its outputs.`,
        c.id,
      );
  for (const i of project.inputs)
    if (!project.connections.some((e) => e.from.input === i.id))
      add(
        "warning",
        `${i.name} is unused. Connect it to an input pin or remove it.`,
      );
  const signalLabels = allSignals(project).map((s) => s.name);
  if (new Set(signalLabels).size !== signalLabels.length)
    add(
      "warning",
      "Some output variable names are duplicated. Use unique labels to keep the timing diagram unambiguous.",
    );
  const adjacency = new Map(project.components.map((c) => [c.id, new Set()]));
  for (const e of project.connections)
    if (
      e.from.component &&
      adjacency.has(e.from.component) &&
      adjacency.has(e.to.component)
    ) {
      adjacency.get(e.from.component).add(e.to.component);
      adjacency.get(e.to.component).add(e.from.component);
    }
  const visited = new Set();
  let sections = 0;
  for (const c of project.components)
    if (!visited.has(c.id)) {
      sections++;
      const todo = [c.id];
      while (todo.length) {
        const id = todo.pop();
        if (visited.has(id)) continue;
        visited.add(id);
        todo.push(...adjacency.get(id));
      }
    }
  if (sections > 1)
    add(
      "warning",
      `The schematic has ${sections} sections without component-to-component connections between them. Independent sections are allowed; verify this is intentional.`,
    );
  const duration = project.timing.duration,
    step = project.timing.step;
  if (
    !Number.isFinite(duration) ||
    duration <= 0 ||
    duration > 4096 ||
    !Number.isFinite(step) ||
    step < 0.05 ||
    step > 4096
  )
    add(
      "error",
      "Timing needs duration > 0 and ≤ 4096, with input step 0.05–4096.",
    );
  if (Number.isFinite(duration / step) && duration / step > 4096)
    add(
      "error",
      "Use at most 4096 input intervals. Increase the input step or reduce duration.",
    );
  for (const i of project.inputs) {
    if (i.values?.some((v) => ![0, 1, "X"].includes(v)))
      add("error", `${i.name}: waveform values must be 0, 1 or X.`);
    if (
      i.events?.some(
        (e, n) =>
          !Number.isFinite(e.time) ||
          e.time < 0 ||
          ![0, 1, "X"].includes(e.value) ||
          (n > 0 && e.time <= i.events[n - 1].time),
      )
    )
      add(
        "error",
        `${i.name}: event times must increase strictly, with values 0, 1 or X.`,
      );
  }
  return {
    valid: !issues.some((i) => i.severity === "error"),
    issues,
    order: order.order,
    drivers: driver,
  };
}
export function canConnect(project, from, to) {
  const target = project.components.find((c) => c.id === to.component);
  if (!target || !pins(target).inputs.some((p) => p.id === to.pin))
    return "Choose an input pin as the destination.";
  if (sourceKey(from) === `${to.component}:${to.pin}`)
    return "An input cannot drive itself.";
  const source = project.components.find((c) => c.id === from.component);
  if (
    !from.input &&
    !(from && "constant" in from) &&
    (!source || !pins(source).outputs.some((p) => p.id === from.pin))
  )
    return "Choose an output pin as the source.";
  if (from.input && !project.inputs.some((i) => i.id === from.input))
    return "The primary input no longer exists.";
  return null;
}
